import confusedClouds from "assets/illustrations/confusedClouds.svg"
import noSearchResult from "assets/illustrations/noSearch.svg"
import { useInfiniteGraphqlQuery } from "graphql-mock"
import { last } from "lodash-es"
import { Card, useErrorSentryLog } from "materia"
import { useTranslation } from "react-i18next"
import { FiestaImage, WhenVisible } from "utility-components"
import type { ExploreContentInput, ProductsInput } from "../../graphql"
import { ProductBundleCard } from "../ProductBundleCard"
import type { Data as ProductBundleCardData } from "../ProductBundleCard/gql"
import { ProductCard, ProductCardsSkeleton } from "../ProductCard"
import type { Data as ProductCardData } from "../ProductCard/gql"
import { ProductList } from "../ProductList"
import { exploreQuery } from "./exploreGql"
import { query } from "./gql"

import s from "./FilteredProducts.module.scss"

const ITEMS_PER_PAGE = 18

export const FilteredProducts = ({ input }: { input: ProductsInput }) => {
  const { data, error, isPending, isFetchingNextPage, fetchNextPage } =
    useInfiniteGraphqlQuery(
      query,
      { input, limit: ITEMS_PER_PAGE },
      { getNextPage: (page) => page.products.pageInfo.nextPage }
    )

  if (isPending) return <FilteredProductsSkeleton count={ITEMS_PER_PAGE} />
  if (error) return <FilteredProductsError error={error} />

  const products = data.pages.flatMap((page) => page.products.nodes)
  const pageInfo = last(data.pages)?.products.pageInfo
  if (!pageInfo) throw new Error("There must be at least one page")

  return (
    <FilteredProductsResults
      input={input}
      items={products.map((product) => ({ product, productBundle: null }))}
      pageInfo={pageInfo}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      pageCount={data.pages.length}
    />
  )
}

/**
 * Explore has its own root field rather than a `variant` on the products query:
 * it is the only surface that mixes bundles into the grid, and the only one that
 * reaches documents living inside a bundle rather than standing alone (FAME-675).
 */
export const ExploreFilteredProducts = ({
  input,
}: {
  input: ExploreContentInput
}) => {
  const { data, error, isPending, isFetchingNextPage, fetchNextPage } =
    useInfiniteGraphqlQuery(
      exploreQuery,
      { input, limit: ITEMS_PER_PAGE },
      { getNextPage: (page) => page.exploreContent.pageInfo.nextPage }
    )

  if (isPending) return <FilteredProductsSkeleton count={ITEMS_PER_PAGE} />
  if (error) return <FilteredProductsError error={error} />

  const items = data.pages.flatMap((page) => page.exploreContent.nodes)
  const pageInfo = last(data.pages)?.exploreContent.pageInfo
  if (!pageInfo) throw new Error("There must be at least one page")

  return (
    <FilteredProductsResults
      input={input}
      items={items}
      pageInfo={pageInfo}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      pageCount={data.pages.length}
    />
  )
}

type Item = {
  product: ProductCardData | null
  productBundle: ProductBundleCardData | null
}

const FilteredProductsResults = ({
  input,
  items,
  pageInfo,
  isFetchingNextPage,
  fetchNextPage,
  pageCount,
}: {
  input: ProductsInput | ExploreContentInput
  items: Item[]
  pageInfo: { nextPageItemCount: number }
  isFetchingNextPage: boolean
  fetchNextPage: () => unknown
  pageCount: number
}) => {
  if (items.length === 0) return <NoResults />

  return (
    <>
      <ProductList>
        {items.map((item) => {
          if (item.product) {
            return (
              <ProductCard
                key={`product-${item.product.id}`}
                data={item.product}
              />
            )
          } else if (item.productBundle) {
            return (
              <ProductBundleCard
                key={`bundle-${item.productBundle.id}`}
                data={item.productBundle}
              />
            )
          } else {
            throw new Error("There must be a product or a bundle")
          }
        })}
        {isFetchingNextPage && (
          <ProductCardsSkeleton count={pageInfo.nextPageItemCount} />
        )}
      </ProductList>

      <WhenVisible
        key={`${pageCount} ${JSON.stringify({ input })}`}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  )
}

const NoResults = () => {
  const { t } = useTranslation()
  return (
    <Card>
      <Card.Content>
        <div className={s.noProducts}>
          <FiestaImage
            alt=""
            sizes="50px"
            src={noSearchResult}
            className={s.noProducts__image}
          />
          <div className={s.noProducts__details}>
            <p className={s.noProducts__title}>{t("filters.NO_PRODUCTS")}</p>
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}

const FilteredProductsSkeleton = ({ count }: { count: number }) => {
  return (
    <ProductList>
      <ProductCardsSkeleton count={count} />
    </ProductList>
  )
}

const FilteredProductsError = ({ error }: { error: unknown }) => {
  // TODO [sentry-logging] https://jira.attensi.com/browse/WEB-18745
  // Find a better place to send Sentry logs from
  useErrorSentryLog(error)
  const { t } = useTranslation()

  return (
    <Card>
      <Card.Content>
        <div className={s.noProducts}>
          <FiestaImage
            alt=""
            sizes="50px"
            src={confusedClouds}
            className={s.noProducts__image}
          />
          <div className={s.noProducts__details}>
            <p className={s.noProducts__title}>{t("error.GENERIC")}</p>
            <p className={s.noProducts__description}>
              {t("error.FILTERED_PRODUCTS_FETCH_ERROR")}
            </p>
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}
